/**
 * Staff (Personal) Booking Dashboard at /app/bookings.
 *
 * Mirrors the HR staff dashboard at /app/members/dashboard. Layout:
 *   container (size="xl")
 *   ├── header (avatar + greeting + refresh icon)
 *   ├── KPI tiles (KpiCard) — Pending / Active / This Year / Years
 *   ├── full-width MyAlertsPanel (compliance + licence warnings)
 *   └── two-column grid
 *       ├── main (~⅔): NextBooking · ActiveBooking · UpcomingBookings
 *       └── sidebar (~⅓): ProfileSnapshotCard · QuickActionsPanel
 *
 * Every widget is scoped to the authenticated user, settings-gated
 * where applicable.
 */
import { useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  ActionIcon,
  Avatar,
  Container,
  Grid,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Tooltip,
  Card,
  Badge,
  Button,
  ThemeIcon,
  Paper,
  Skeleton,
  Alert,
  Divider,
} from '@/components/ui';
import { FadeIn } from '@/components/animations';
import KpiCard from '@/components/advanced/Widgets/kpi/KpiCard';
import {
  IconRefresh,
  IconPlus,
  IconCalendarEvent,
  IconClock,
  IconCircleCheck,
  IconAward,
  IconPlayerPlay,
  IconCheck,
  IconRoute,
  IconLicense,
  IconCar,
  IconBuilding,
  IconDeviceLaptop,
  IconArrowRight,
  IconBell,
  IconAlertTriangle,
  IconUserCircle,
  IconClipboardList,
} from '@tabler/icons-react';
import {
  useBookingService,
  useUserDriverProfileService,
} from '@actschurch/ccms-sdk';
import type { BookingModelView, UserDriverProfile } from '@actschurch/ccms-sdk';
import { BookingStatusBadge } from '../components';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import {
  formatBookingDateTime,
  greetingForHour,
  initials,
  parseCcmsTimestamp,
} from '../utils/bookingHelpers';
import { usePlatformSettings } from '../hooks/usePlatformSettings';

const STATUS = {
  Pending: 0, Approved: 1, Active: 2, Completed: 3,
  Rejected: -1, Cancelled: -2, Overdue: -3, Incomplete: -4,
} as const;

const KEY = ['bookings', 'staff', 'dashboard'] as const;

function todayInt(): number {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return Number(`${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`);
}
function ccmsDateInt(ts?: number | null): number | null {
  if (!ts) return null;
  return Number(String(ts).padStart(14, '0').slice(0, 8));
}
function daysUntil(ts?: number | null): number | null {
  if (!ts) return null;
  const d = parseCcmsTimestamp(ts);
  if (!d) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86_400_000);
}

export default function BookingDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, getUserDisplayName } = useEnrichedAuth();
  const displayName = getUserDisplayName();
  const userId = user?.id ?? '';

  const bookingService = useBookingService();
  const driverProfileService = useUserDriverProfileService();
  const platform = usePlatformSettings();

  const { data: bookings = [], isLoading } = useQuery<BookingModelView[]>({
    queryKey: [...KEY, 'my-bookings', userId],
    queryFn: () => bookingService.getMyBookings() as unknown as Promise<BookingModelView[]>,
    enabled: !!user,
  });

  const { data: driverProfile } = useQuery<UserDriverProfile | null>({
    queryKey: [...KEY, 'driver-profile', userId],
    queryFn: () => driverProfileService.getForUser(userId),
    enabled: !!userId && platform.enableDriverProfileCheck,
  });

  const active = useMemo(
    () => bookings.find((b) => b.bookingStatusID === STATUS.Active),
    [bookings],
  );
  const upcoming = useMemo(() => {
    const today = todayInt();
    return bookings
      .filter((b) =>
        (b.bookingStatusID === STATUS.Pending || b.bookingStatusID === STATUS.Approved)
        && (ccmsDateInt(b.startDateTime) ?? 0) >= today,
      )
      .sort((a, b) => (a.startDateTime || 0) - (b.startDateTime || 0));
  }, [bookings]);
  const nextBooking = upcoming[0];
  const restUpcoming = upcoming.slice(1, 4);
  const nonCompliantCount = useMemo(
    () => bookings.filter((b) =>
      b.bookingStatusID === STATUS.Overdue || b.bookingStatusID === STATUS.Incomplete).length,
    [bookings],
  );
  const yearStats = useMemo(() => {
    const yearStart = Number(`${new Date().getFullYear()}0101000000`);
    const thisYear = bookings.filter((b) => (b.createdDateTime || 0) >= yearStart);
    const completed = thisYear.filter((b) => b.bookingStatusID === STATUS.Completed).length;
    const total = thisYear.length;
    const completionRate = total ? Math.round((completed / total) * 100) : 0;
    return { total, completed, completionRate };
  }, [bookings]);

  const driverDaysLeft = daysUntil(driverProfile?.licenseExpiry);
  const licenceExpiringSoon = driverDaysLeft != null && driverDaysLeft <= 60 && driverDaysLeft >= 0;
  const licenceExpired = driverDaysLeft != null && driverDaysLeft < 0;

  // Tenure: account creation → today (best proxy we have for the user landing).
  const yearsAtCompany = useMemo(() => {
    const startTs = bookings.reduce((min, b) => {
      const v = b.createdDateTime ?? 0;
      return v > 0 && (min === 0 || v < min) ? v : min;
    }, 0);
    const d = parseCcmsTimestamp(startTs || null);
    if (!d) return 0;
    return Math.max(0, dayjs().diff(dayjs(d), 'year'));
  }, [bookings]);

  const handleRefresh = () => queryClient.invalidateQueries({ queryKey: KEY as unknown as readonly unknown[] });

  const greeting = useMemo(() => {
    const base = greetingForHour();
    const first = (displayName ?? '').split(/\s+/)[0];
    return first ? `${base}, ${first}` : base;
  }, [displayName]);

  return (
    <Container size="xl" py="md">
      <FadeIn>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group gap="md" wrap="nowrap">
              <Avatar src={user?.avatar ?? undefined} size="lg" radius="xl" color="indigo">
                {initials(displayName)}
              </Avatar>
              <Stack gap={2}>
                <Title order={2}>{isLoading ? 'Loading…' : greeting}</Title>
                <Text size="sm" c="dimmed">Your bookings at a glance.</Text>
              </Stack>
            </Group>
            <Tooltip label="Refresh dashboard">
              <ActionIcon variant="light" size="lg" radius="xl" onClick={handleRefresh} aria-label="Refresh">
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>

          {/* KPI tiles */}
          {isLoading ? (
            <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={150} radius="md" />)}
            </SimpleGrid>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
              <FadeIn>
                <KpiCard title="Pending Requests" value={upcoming.filter((b) => b.bookingStatusID === STATUS.Pending).length}
                  icon={<IconClock size={20} />} color="yellow" animate />
              </FadeIn>
              <FadeIn delay={60}>
                <KpiCard title="Active Now" value={active ? 1 : 0}
                  icon={<IconPlayerPlay size={20} />} color="green" animate />
              </FadeIn>
              <FadeIn delay={120}>
                <KpiCard title="This Year" value={yearStats.total}
                  description={yearStats.total > 0 ? `${yearStats.completionRate}% completed` : 'No bookings yet'}
                  icon={<IconCircleCheck size={20} />} color="blue" animate />
              </FadeIn>
              <FadeIn delay={180}>
                <KpiCard title="Years Using Bookings" value={yearsAtCompany}
                  icon={<IconAward size={20} />} color="grape" animate />
              </FadeIn>
            </SimpleGrid>
          )}

          {/* Alerts panel */}
          <MyAlertsPanel
            nonCompliantCount={nonCompliantCount}
            licenceExpired={licenceExpired}
            licenceExpiringSoon={licenceExpiringSoon}
            driverDaysLeft={driverDaysLeft}
            showLicence={platform.enableDriverProfileCheck}
          />

          {/* Two-column grid */}
          <Grid gutter="md">
            <Grid.Col span={{ base: 12, lg: 8 }}>
              <Stack gap="md">
                {active && (
                  <ActiveBookingCard
                    booking={active}
                    onView={() => navigate(`/app/bookings/view/${active.bookingID}`)}
                    onComplete={() => navigate(`/app/bookings/log/post/${active.bookingID}`)}
                  />
                )}
                {nextBooking && (
                  <NextBookingCard
                    booking={nextBooking}
                    onView={() => navigate(`/app/bookings/view/${nextBooking.bookingID}`)}
                    onStart={() => navigate(`/app/bookings/log/pre/${nextBooking.bookingID}`)}
                  />
                )}
                <UpcomingBookingsPanel
                  bookings={restUpcoming}
                  empty={!nextBooking && !active}
                  blocked={nonCompliantCount > 0}
                  onView={(id) => navigate(`/app/bookings/view/${id}`)}
                  onNew={() => navigate('/app/bookings/create')}
                  onViewAll={() => navigate('/app/bookings/my-bookings')}
                />
              </Stack>
            </Grid.Col>
            <Grid.Col span={{ base: 12, lg: 4 }}>
              <Stack gap="md">
                <ProfileSnapshotCard
                  displayName={displayName}
                  avatar={user?.avatar ?? null}
                  email={user?.email ?? null}
                  driverProfile={driverProfile ?? null}
                  driverDaysLeft={driverDaysLeft}
                  licenceExpired={licenceExpired}
                  showLicence={platform.enableDriverProfileCheck}
                />
                <QuickActionsPanel blocked={nonCompliantCount > 0} />
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      </FadeIn>
    </Container>
  );
}

// ───────────────────────── Panels ─────────────────────────

function MyAlertsPanel({
  nonCompliantCount, licenceExpired, licenceExpiringSoon, driverDaysLeft, showLicence,
}: {
  nonCompliantCount: number;
  licenceExpired: boolean;
  licenceExpiringSoon: boolean;
  driverDaysLeft: number | null;
  showLicence: boolean;
}) {
  if (nonCompliantCount === 0 && !(showLicence && (licenceExpired || licenceExpiringSoon))) return null;
  return (
    <Stack gap="sm">
      {nonCompliantCount > 0 && (
        <Alert
          color="red"
          alertVariant="light"
          icon={<IconAlertTriangle size={18} />}
          title="You have unresolved bookings"
        >
          {nonCompliantCount} overdue or incomplete booking{nonCompliantCount === 1 ? '' : 's'} blocking new requests. Complete or cancel them to unblock yourself.
        </Alert>
      )}
      {showLicence && (licenceExpired || licenceExpiringSoon) && (
        <Alert
          color={licenceExpired ? 'red' : 'orange'}
          alertVariant="light"
          icon={<IconLicense size={18} />}
          title={licenceExpired
            ? 'Driver licence expired'
            : `Driver licence expires in ${driverDaysLeft} day${driverDaysLeft === 1 ? '' : 's'}`}
        >
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Text size="sm">Vehicle bookings require a valid licence. Renew with your admin to keep booking.</Text>
            <Button size="xs" variant="light" component={Link} to="/app/bookings/my-licences">View my licence</Button>
          </Group>
        </Alert>
      )}
    </Stack>
  );
}

function ActiveBookingCard({
  booking, onView, onComplete,
}: { booking: BookingModelView; onView: () => void; onComplete: () => void }) {
  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <ThemeIcon size="md" variant="light" color="green" radius="xl">
            <IconPlayerPlay size={16} />
          </ThemeIcon>
          <Title order={5}>Active now</Title>
        </Group>
        <Badge color="green" variant="filled" size="sm">Checked out</Badge>
      </Group>
      <Stack gap={2}>
        <Text fw={600}>{booking.resourceName || `Booking #${booking.bookingID}`}</Text>
        <Text size="sm" c="dimmed">
          Out {formatBookingDateTime(booking.actualStartDateTime || booking.startDateTime)} • Due back {formatBookingDateTime(booking.endDateTime)}
        </Text>
        {booking.destinationDisplay && (
          <Group gap={4} mt={4}>
            <IconRoute size={12} color="var(--mantine-color-dimmed)" />
            <Text size="xs" c="dimmed">{booking.destinationDisplay}</Text>
          </Group>
        )}
      </Stack>
      <Group gap="sm" mt="md">
        <Button variant="light" onClick={onView}>View</Button>
        <Button color="teal" leftSection={<IconCheck size={16} />} onClick={onComplete}>
          Complete (post-log)
        </Button>
      </Group>
    </Card>
  );
}

function NextBookingCard({
  booking, onView, onStart,
}: { booking: BookingModelView; onView: () => void; onStart: () => void }) {
  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <ThemeIcon size="md" variant="light" color="blue" radius="xl">
            <IconCalendarEvent size={16} />
          </ThemeIcon>
          <Title order={5}>Up next</Title>
        </Group>
        <BookingStatusBadge status={booking.bookingStatusID} size="sm" />
      </Group>
      <Stack gap={2}>
        <Text fw={600}>{booking.resourceName || `Booking #${booking.bookingID}`}</Text>
        <Text size="sm" c="dimmed">
          {formatBookingDateTime(booking.startDateTime)} – {formatBookingDateTime(booking.endDateTime)}
        </Text>
        {booking.destinationDisplay && (
          <Group gap={4} mt={4}>
            <IconRoute size={12} color="var(--mantine-color-dimmed)" />
            <Text size="xs" c="dimmed">
              {booking.destinationDisplay}
              {booking.plannedDistanceKm != null && ` • ${booking.plannedDistanceKm} km planned`}
            </Text>
          </Group>
        )}
      </Stack>
      <Group gap="sm" mt="md">
        <Button variant="light" onClick={onView}>View</Button>
        {booking.bookingStatusID === STATUS.Approved && (
          <Button color="green" leftSection={<IconPlayerPlay size={16} />} onClick={onStart}>
            Start (pre-log)
          </Button>
        )}
      </Group>
    </Card>
  );
}

function UpcomingBookingsPanel({
  bookings, empty, blocked, onView, onNew, onViewAll,
}: {
  bookings: BookingModelView[];
  empty: boolean;
  blocked: boolean;
  onView: (id: number) => void;
  onNew: () => void;
  onViewAll: () => void;
}) {
  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <ThemeIcon size="md" variant="light" color="blue" radius="xl">
            <IconBell size={16} />
          </ThemeIcon>
          <Title order={5}>Upcoming</Title>
        </Group>
        <Button variant="subtle" size="xs" rightSection={<IconArrowRight size={14} />} onClick={onViewAll}>
          View all
        </Button>
      </Group>

      {empty ? (
        <Stack align="center" py="lg" gap="xs">
          <ThemeIcon size="xl" radius="xl" variant="light" color="gray">
            <IconCalendarEvent size={24} />
          </ThemeIcon>
          <Text c="dimmed" size="sm">No upcoming bookings</Text>
          <Button variant="light" size="xs" leftSection={<IconPlus size={14} />} onClick={onNew} disabled={blocked}>
            Book something
          </Button>
        </Stack>
      ) : bookings.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="md">Just the one above for now.</Text>
      ) : (
        <Stack gap="xs">
          {bookings.map((b) => (
            <Paper
              key={b.bookingID}
              withBorder p="sm" radius="sm"
              style={{ cursor: 'pointer' }}
              onClick={() => onView(b.bookingID)}
            >
              <Group justify="space-between" wrap="nowrap">
                <Stack gap={2}>
                  <Text size="sm" fw={500}>{b.resourceName || `Booking #${b.bookingID}`}</Text>
                  <Text size="xs" c="dimmed">
                    {formatBookingDateTime(b.startDateTime)}{b.destinationDisplay && ` • ${b.destinationDisplay}`}
                  </Text>
                </Stack>
                <BookingStatusBadge status={b.bookingStatusID} size="xs" />
              </Group>
            </Paper>
          ))}
        </Stack>
      )}
    </Card>
  );
}

function ProfileSnapshotCard({
  displayName, avatar, email, driverProfile, driverDaysLeft, licenceExpired, showLicence,
}: {
  displayName: string;
  avatar: string | null;
  email: string | null;
  driverProfile: UserDriverProfile | null;
  driverDaysLeft: number | null;
  licenceExpired: boolean;
  showLicence: boolean;
}) {
  const classes = driverProfile?.licenseClasses?.split(',').map((s) => s.trim()).filter(Boolean) ?? [];
  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <ThemeIcon size="md" variant="light" color="violet" radius="xl">
            <IconUserCircle size={16} />
          </ThemeIcon>
          <Title order={5}>Profile Snapshot</Title>
        </Group>
      </Group>
      <Stack gap="md">
        <Group gap="sm" wrap="nowrap">
          <Avatar src={avatar ?? undefined} size="lg" radius="xl" color="violet">
            {initials(displayName)}
          </Avatar>
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text size="sm" fw={600} truncate="end">{displayName || '—'}</Text>
            {email && <Text size="xs" c="dimmed" truncate="end">{email}</Text>}
          </Stack>
        </Group>
        {showLicence && (
          <>
            <Divider />
            <Stack gap={4}>
              <Text size="xs" c="dimmed" tt="uppercase">Driver licence</Text>
              {driverProfile ? (
                <>
                  <Group gap="xs">
                    {classes.length === 0 && <Text size="sm">—</Text>}
                    {classes.map((c) => (
                      <Badge key={c} size="sm" variant="filled">{c}</Badge>
                    ))}
                  </Group>
                  <Text size="xs" c={licenceExpired ? 'red' : 'dimmed'}>
                    {licenceExpired
                      ? `Expired ${Math.abs(driverDaysLeft!)} day${Math.abs(driverDaysLeft!) === 1 ? '' : 's'} ago`
                      : driverDaysLeft != null
                        ? `Expires in ${driverDaysLeft} day${driverDaysLeft === 1 ? '' : 's'}`
                        : 'Active'}
                  </Text>
                </>
              ) : (
                <Text size="xs" c="dimmed">No licence on file. Ask your admin to register one.</Text>
              )}
            </Stack>
          </>
        )}
        <Button
          size="xs"
          variant="light"
          fullWidth
          component={Link}
          to="/app/bookings/my-licences"
        >
          Manage licence
        </Button>
      </Stack>
    </Card>
  );
}

function QuickActionsPanel({ blocked }: { blocked: boolean }) {
  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <ThemeIcon size="md" variant="light" color="teal" radius="xl">
            <IconPlus size={16} />
          </ThemeIcon>
          <Title order={5}>Quick Actions</Title>
        </Group>
      </Group>
      <Stack gap="xs">
        <Button
          variant="light" fullWidth justify="flex-start"
          leftSection={<IconCar size={16} />}
          component={Link} to="/app/bookings/create?category=Vehicle"
          disabled={blocked}
        >
          Book a vehicle
        </Button>
        <Button
          variant="light" fullWidth justify="flex-start"
          leftSection={<IconBuilding size={16} />}
          component={Link} to="/app/bookings/create?category=Room"
          disabled={blocked}
        >
          Book a room
        </Button>
        <Button
          variant="light" fullWidth justify="flex-start"
          leftSection={<IconDeviceLaptop size={16} />}
          component={Link} to="/app/bookings/create?category=Equipment"
          disabled={blocked}
        >
          Book equipment
        </Button>
        <Divider my={4} />
        <Button
          variant="subtle" fullWidth justify="flex-start"
          leftSection={<IconClipboardList size={16} />}
          component={Link} to="/app/bookings/my-bookings"
        >
          View my bookings
        </Button>
      </Stack>
    </Card>
  );
}
